(function () {
  'use strict';

  var ELEMENT_NAME = 'sf-beam-explorer';
  var MIN_FRAME_COUNT = 30;
  var MAX_FRAME_COUNT = 36;
  var MAX_FRAME_BYTES = 320 * 1024;
  var MAX_BLOB_BYTES = 2.5 * 1024 * 1024;
  var MAX_DECODED_FRAMES = 8;
  var MAX_FETCHES = 2;
  var PREFETCH_RADIUS = 3;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function normalizeAnimationSource(value) {
    if (value === 'custom_manifest' || value === 'static_image') return value;
    return 'built_in_1097';
  }

  function normalizePosition(value) {
    var parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? clamp(parsedValue, 0, 100) : 50;
  }

  function normalizeManifest(rawManifest, baseUrl) {
    if (!rawManifest || Number(rawManifest.version) !== 1) {
      throw new Error('Unsupported frame manifest version.');
    }

    var width = Number(rawManifest.width);
    var height = Number(rawManifest.height);
    var frames = rawManifest.frames;

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || width > 4000 || height > 4000 || width * height > 8000000) {
      throw new Error('Invalid frame dimensions.');
    }

    if (!Array.isArray(frames) || frames.length < MIN_FRAME_COUNT || frames.length > MAX_FRAME_COUNT) {
      throw new Error('The frame manifest must contain 30 to 36 frames.');
    }

    var normalizedFrames = frames.map(function (frame) {
      var source = typeof frame === 'string' ? frame : frame && frame.src;
      if (!source) {
        throw new Error('A frame URL is missing.');
      }

      var parsedUrl = new URL(source, baseUrl || document.baseURI);
      if (!/^https?:$/.test(parsedUrl.protocol)) {
        throw new Error('A frame URL uses an unsupported protocol.');
      }
      return parsedUrl.href;
    });

    return {
      version: 1,
      width: Math.round(width),
      height: Math.round(height),
      frames: normalizedFrames
    };
  }

  function decodeImageElement(blob) {
    return new Promise(function (resolve, reject) {
      var objectUrl = URL.createObjectURL(blob);
      var image = new Image();
      image.decoding = 'async';
      image.onload = function () {
        URL.revokeObjectURL(objectUrl);
        resolve({ drawable: image, dispose: function () {} });
      };
      image.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to decode a frame.'));
      };
      image.src = objectUrl;
    });
  }

  function decodeBlob(blob) {
    if ('createImageBitmap' in window) {
      return createImageBitmap(blob).then(function (bitmap) {
        return {
          drawable: bitmap,
          dispose: function () {
            if (typeof bitmap.close === 'function') bitmap.close();
          }
        };
      }).catch(function () {
        return decodeImageElement(blob);
      });
    }

    return decodeImageElement(blob);
  }

  function FrameStore(manifest, onFrameReady, onFrameError) {
    this.manifest = manifest;
    this.onFrameReady = onFrameReady;
    this.onFrameError = onFrameError;
    this.decoded = new Map();
    this.blobs = new Map();
    this.blobBytes = 0;
    this.queue = [];
    this.scheduled = new Set();
    this.active = new Map();
    this.activeCount = 0;
    this.decodeTail = Promise.resolve();
    this.targetIndex = 0;
    this.pinnedIndex = -1;
    this.backgroundEnabled = false;
    this.generation = 0;
    this.desiredIndices = new Set();
    this.destroyed = false;
  }

  FrameStore.prototype.getDecoded = function (index) {
    var frame = this.decoded.get(index);
    if (frame) frame.lastUsed = performance.now();
    return frame || null;
  };

  FrameStore.prototype.getClosestDecoded = function (targetIndex, direction, displayedIndex) {
    var path = [];
    var directional = [];
    var fallback = [];
    var pathStart = Math.min(displayedIndex, targetIndex);
    var pathEnd = Math.max(displayedIndex, targetIndex);

    this.decoded.forEach(function (frame, index) {
      var candidate = { index: index, frame: frame, distance: Math.abs(index - targetIndex) };
      fallback.push(candidate);
      if (displayedIndex >= 0 && index >= pathStart && index <= pathEnd) path.push(candidate);
      if ((direction >= 0 && index <= targetIndex) || (direction < 0 && index >= targetIndex)) {
        directional.push(candidate);
      }
    });

    var displayed = displayedIndex >= 0 ? this.decoded.get(displayedIndex) : null;
    if (!path.length && displayed) {
      displayed.lastUsed = performance.now();
      return { index: displayedIndex, frame: displayed };
    }

    var candidates = path.length ? path : directional.length ? directional : fallback;
    candidates.sort(function (left, right) {
      if (left.distance !== right.distance) return left.distance - right.distance;
      return right.frame.lastUsed - left.frame.lastUsed;
    });

    var closest = candidates[0] || null;
    if (closest) closest.frame.lastUsed = performance.now();
    return closest;
  };

  FrameStore.prototype.setPinnedIndex = function (index) {
    this.pinnedIndex = index;
  };

  FrameStore.prototype.requestTarget = function (index, direction) {
    if (this.destroyed) return;
    this.targetIndex = clamp(index, 0, this.manifest.frames.length - 1);
    if (this.backgroundEnabled) {
      this.reconcileWindow(this.targetIndex, direction || 1);
    } else {
      this.cancelPending();
      this.enqueue(this.targetIndex, true);
    }
  };

  FrameStore.prototype.resumePrefetch = function (targetIndex, direction) {
    if (this.destroyed) return;
    this.backgroundEnabled = true;
    this.targetIndex = clamp(targetIndex, 0, this.manifest.frames.length - 1);
    this.reconcileWindow(this.targetIndex, direction || 1);
  };

  FrameStore.prototype.pausePrefetch = function () {
    this.backgroundEnabled = false;
    this.cancelPending();
  };

  FrameStore.prototype.buildWindow = function (targetIndex, direction) {
    var frameCount = this.manifest.frames.length;
    var order = [targetIndex];
    var step;

    for (step = 1; step <= PREFETCH_RADIUS; step += 1) {
      var first = targetIndex + step * direction;
      var second = targetIndex - step * direction;
      if (first >= 0 && first < frameCount) order.push(first);
      if (second >= 0 && second < frameCount) order.push(second);
    }
    return order;
  };

  FrameStore.prototype.reconcileWindow = function (targetIndex, direction) {
    var self = this;
    var order = this.buildWindow(targetIndex, direction);
    var allowed = new Set(order);
    this.generation += 1;
    this.desiredIndices = allowed;

    this.queue = this.queue.filter(function (job) {
      if (!allowed.has(job.index)) {
        self.scheduled.delete(job.index);
        return false;
      }
      job.priority = job.index === targetIndex;
      job.generation = self.generation;
      return true;
    });
    this.queue.sort(function (left, right) {
      return order.indexOf(left.index) - order.indexOf(right.index);
    });

    this.active.forEach(function (job, index) {
      if (!allowed.has(index)) {
        job.retry = false;
        job.retryPriority = false;
        job.controller.abort();
      } else {
        job.priority = index === targetIndex;
        job.generation = self.generation;
      }
    });

    for (var index = 0; index < order.length; index += 1) {
      this.enqueue(order[index], order[index] === targetIndex);
    }
  };

  FrameStore.prototype.cancelPending = function () {
    var self = this;
    this.generation += 1;
    this.desiredIndices = new Set();
    this.queue.forEach(function (job) { self.scheduled.delete(job.index); });
    this.queue.length = 0;
    this.active.forEach(function (job) {
      job.retry = false;
      job.retryPriority = false;
      job.controller.abort();
    });
  };

  FrameStore.prototype.enqueue = function (index, priority) {
    if (this.destroyed || this.decoded.has(index)) return;

    if (this.scheduled.has(index)) {
      var activeJob = this.active.get(index);
      if (activeJob && activeJob.controller.signal.aborted) {
        activeJob.retry = true;
        activeJob.retryPriority = Boolean(priority);
        activeJob.retryGeneration = this.generation;
        return;
      }
      if (priority) {
        var queuedIndex = this.queue.findIndex(function (job) { return job.index === index; });
        if (queuedIndex >= 0) {
          var queuedJob = this.queue.splice(queuedIndex, 1)[0];
          queuedJob.priority = true;
          queuedJob.generation = this.generation;
          this.queue.unshift(queuedJob);
        } else if (this.active.has(index)) {
          this.active.get(index).priority = true;
        }
      }
      return;
    }

    var job = { index: index, priority: Boolean(priority), controller: null, generation: this.generation };
    this.scheduled.add(index);
    if (priority) this.queue.unshift(job);
    else this.queue.push(job);
    this.pump();
  };

  FrameStore.prototype.pump = function () {
    var self = this;

    while (!this.destroyed && this.activeCount < MAX_FETCHES && this.queue.length) {
      let job = this.queue.shift();
      if (!job.priority && !this.backgroundEnabled) {
        this.scheduled.delete(job.index);
        continue;
      }

      job.controller = new AbortController();
      this.active.set(job.index, job);
      this.activeCount += 1;

      this.load(job.index, job.controller.signal)
        .then(function (loadedIndex) {
          if (!self.destroyed && typeof self.onFrameReady === 'function') {
            self.onFrameReady(loadedIndex);
          }
        })
        .catch(function (error) {
          if (error && error.name !== 'AbortError') {
            console.warn('[SkyFire beam explorer] A frame could not be loaded.', error);
            if (typeof self.onFrameError === 'function') self.onFrameError(job.index, error, self);
          }
        })
        .finally(function () {
          var retry = Boolean(
            job.retry &&
            !self.destroyed &&
            self.backgroundEnabled &&
            job.retryGeneration === self.generation &&
            self.desiredIndices.has(job.index)
          );
          self.active.delete(job.index);
          self.scheduled.delete(job.index);
          self.activeCount = Math.max(0, self.activeCount - 1);
          if (retry) self.enqueue(job.index, job.retryPriority);
          self.pump();
        });
    }
  };

  FrameStore.prototype.load = async function (index, signal) {
    if (this.destroyed || this.decoded.has(index)) return index;

    var blob = this.blobs.get(index);
    if (!blob) {
      var response = await fetch(this.manifest.frames[index], {
        cache: 'force-cache',
        credentials: 'same-origin',
        signal: signal
      });
      if (!response.ok) throw new Error('Frame request failed with status ' + response.status + '.');

      blob = await response.blob();
      if (!blob.size || blob.size > MAX_FRAME_BYTES) {
        throw new Error('Frame file size is outside the allowed range.');
      }

      if (this.blobBytes + blob.size <= MAX_BLOB_BYTES) {
        this.blobs.set(index, blob);
        this.blobBytes += blob.size;
      }
    }

    var decodeTask = this.decodeTail.then(function () {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      return decodeBlob(blob);
    });
    this.decodeTail = decodeTask.catch(function () {});

    var decodedFrame = await decodeTask;
    if (this.destroyed || signal.aborted) {
      decodedFrame.dispose();
      throw new DOMException('Aborted', 'AbortError');
    }

    this.decoded.set(index, {
      drawable: decodedFrame.drawable,
      dispose: decodedFrame.dispose,
      lastUsed: performance.now()
    });
    this.evictDecoded();
    return index;
  };

  FrameStore.prototype.evictDecoded = function () {
    var self = this;
    if (this.decoded.size <= MAX_DECODED_FRAMES) return;

    var candidates = Array.from(this.decoded.entries())
      .filter(function (entry) { return entry[0] !== self.targetIndex && entry[0] !== self.pinnedIndex; })
      .sort(function (left, right) {
        var leftDistance = Math.abs(left[0] - self.targetIndex);
        var rightDistance = Math.abs(right[0] - self.targetIndex);
        if (leftDistance !== rightDistance) return rightDistance - leftDistance;
        return left[1].lastUsed - right[1].lastUsed;
      });

    while (this.decoded.size > MAX_DECODED_FRAMES && candidates.length) {
      var entry = candidates.shift();
      this.decoded.delete(entry[0]);
      entry[1].dispose();
    }
  };

  FrameStore.prototype.destroy = function () {
    this.destroyed = true;
    this.queue.length = 0;
    this.scheduled.clear();
    this.active.forEach(function (job) { job.controller.abort(); });
    this.active.clear();
    this.decoded.forEach(function (frame) { frame.dispose(); });
    this.decoded.clear();
    this.blobs.clear();
    this.blobBytes = 0;
  };

  class SkyFireBeamExplorer extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      this._lifecycle = new AbortController();
      this._runtimeToken = 0;
      this._runtimeStarted = false;
      this._nearViewport = false;
      this._visible = !document.hidden;
      this._frameStore = null;
      this._manifest = null;
      this._animationSource = normalizeAnimationSource(this.dataset.animationSource);
      this._zoomMediaFit = this.dataset.zoomMediaFit === 'cover' ? 'cover' : 'contain';
      this._zoomPositionX = normalizePosition(this.dataset.zoomPositionX);
      this._zoomPositionY = normalizePosition(this.dataset.zoomPositionY);
      this._customManifestUrl = (this.dataset.frameManifestUrl || '').trim();
      this._zoomControlsAvailable = this._animationSource !== 'static_image'
        && (this._animationSource !== 'custom_manifest' || Boolean(this._customManifestUrl));
      this._runtimeFailed = false;
      this._customFrameErrors = 0;
      this._customFrameFailureScheduled = false;
      this._renderRequest = 0;
      this._resizeRequest = 0;
      this._inputRequest = 0;
      this._displayedFrame = -1;
      this._previousTarget = 0;
      this._lastDirection = 1;

      this._canvas = this.querySelector('[data-sf-frame-canvas]');
      this._poster = this.querySelector('[data-sf-frame-poster]');
      this._zoomStage = this.querySelector('[data-sf-zoom-media]');
      this._scenesStage = this.querySelector('[data-sf-scenes-media]');
      this._zoomControls = this.querySelector('[data-sf-zoom-controls]');
      this._scenesControls = this.querySelector('[data-sf-scenes-controls]');
      this._range = this.querySelector('[data-sf-zoom-range]');
      this._stateLabel = this.querySelector('[data-sf-zoom-state-label]');
      this._modeButtons = Array.from(this.querySelectorAll('[data-sf-mode-button]'));
      this._sceneButtons = Array.from(this.querySelectorAll('[data-sf-scene-button]'));
      this._scenePanels = Array.from(this.querySelectorAll('[data-sf-scene-panel]'));
      this._mode = this.dataset.defaultMode === 'scenes' && this._scenesStage ? 'scenes' : 'zoom';

      if (!this._canvas || !this._zoomStage || !this._range) return;

      this._range.disabled = true;
      this.applyZoomMediaPresentation();
      this._value = clamp(Number(this.dataset.defaultZoom) || Number(this._range.value) || 0, 0, 100);
      this._range.value = String(this._value);
      this.updateRangePresentation();
      this.bindEvents();
      this.selectMode(this._mode, false);
      if (this._animationSource === 'static_image') {
        this.updateZoomControlsVisibility();
      } else {
        this.observeViewport();
        this.observeSize();
      }
    }

    disconnectedCallback() {
      this._mounted = false;
      this._runtimeToken += 1;
      if (this._lifecycle) this._lifecycle.abort();
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._renderRequest) cancelAnimationFrame(this._renderRequest);
      if (this._resizeRequest) cancelAnimationFrame(this._resizeRequest);
      if (this._inputRequest) cancelAnimationFrame(this._inputRequest);
      if (this._frameStore) this._frameStore.destroy();
      this._frameStore = null;
    }

    applyZoomMediaPresentation() {
      if (!this._poster) return;
      this._poster.style.objectFit = this._zoomMediaFit;
      this._poster.style.objectPosition = this._zoomPositionX + '% ' + this._zoomPositionY + '%';
    }

    updateZoomControlsVisibility() {
      if (this._zoomControls) {
        this._zoomControls.hidden = this._mode !== 'zoom' || !this._zoomControlsAvailable;
      }

      if (this._stateLabel) {
        var showStateLabel = this._mode === 'zoom' && this._zoomControlsAvailable;
        this._stateLabel.hidden = !showStateLabel;
        this._stateLabel.style.display = showStateLabel ? '' : 'none';
      }
    }

    disableFrameInteraction() {
      this._runtimeFailed = true;
      this._runtimeStarted = true;
      this._zoomControlsAvailable = false;
      this._range.disabled = true;
      this._manifest = null;
      this._displayedFrame = -1;

      if (this._frameStore) this._frameStore.destroy();
      this._frameStore = null;
      if (this._zoomStage) this._zoomStage.removeAttribute('data-frame-ready');
      this.updateZoomControlsVisibility();
    }

    bindEvents() {
      var self = this;
      var signal = this._lifecycle.signal;

      this._range.addEventListener('input', function () {
        var nextValue = clamp(Number(self._range.value) || 0, 0, 100);
        self._value = nextValue;
        self.updateRangePresentation();
        self.scheduleFrameRequest();
      }, { signal: signal });

      this._modeButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          self.selectMode(button.dataset.mode, true);
        }, { signal: signal });
        button.addEventListener('keydown', function (event) {
          self.handleTabKeydown(event, self._modeButtons);
        }, { signal: signal });
      });

      this._sceneButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          self.selectScene(button.dataset.sceneId, true);
        }, { signal: signal });
        button.addEventListener('keydown', function (event) {
          self.handleTabKeydown(event, self._sceneButtons);
        }, { signal: signal });
      });

      document.addEventListener('visibilitychange', function () {
        self._visible = !document.hidden;
        self.syncPrefetchState();
      }, { signal: signal });

      document.addEventListener('shopify:block:select', function (event) {
        var blockId = event.detail && event.detail.blockId;
        var hasScene = self._sceneButtons.some(function (button) {
          return button.dataset.sceneId === blockId;
        });
        if (!blockId || !hasScene) return;
        self.selectMode('scenes', false);
        self.selectScene(blockId, false);
      }, { signal: signal });
    }

    handleTabKeydown(event, buttons) {
      var key = event.key;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;
      event.preventDefault();

      var currentIndex = buttons.indexOf(event.currentTarget);
      var nextIndex = currentIndex;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = buttons.length - 1;
      if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
      buttons[nextIndex].focus();
      buttons[nextIndex].click();
    }

    observeViewport() {
      var self = this;
      if (this._animationSource === 'static_image' || this._runtimeFailed) return;
      if (!('IntersectionObserver' in window)) {
        this._nearViewport = true;
        this.startRuntime();
        return;
      }

      this._intersectionObserver = new IntersectionObserver(function (entries) {
        self._nearViewport = Boolean(entries[0] && entries[0].isIntersecting);
        if (self._nearViewport) self.startRuntime();
        self.syncPrefetchState();
      }, { rootMargin: '300px 0px', threshold: 0.01 });
      this._intersectionObserver.observe(this);
    }

    observeSize() {
      var self = this;
      if (!('ResizeObserver' in window)) {
        window.addEventListener('resize', function () { self.scheduleResize(); }, { signal: this._lifecycle.signal });
        this.scheduleResize();
        return;
      }

      this._resizeObserver = new ResizeObserver(function () { self.scheduleResize(); });
      this._resizeObserver.observe(this._zoomStage);
      this.scheduleResize();
    }

    scheduleResize() {
      var self = this;
      if (this._resizeRequest) return;
      this._resizeRequest = requestAnimationFrame(function () {
        self._resizeRequest = 0;
        self.resizeCanvas();
      });
    }

    scheduleFrameRequest() {
      var self = this;
      if (this._inputRequest) return;
      this._inputRequest = requestAnimationFrame(function () {
        self._inputRequest = 0;
        self.requestCurrentFrame();
      });
    }

    resizeCanvas() {
      if (!this._canvas || !this._zoomStage) return;
      var rect = this._zoomStage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      var sourceWidth = this._manifest ? this._manifest.width : 960;
      var sourceHeight = this._manifest ? this._manifest.height : 480;
      var pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      var canvasWidth = Math.max(1, Math.min(Math.round(rect.width * pixelRatio), sourceWidth * 2));
      var canvasHeight = Math.max(1, Math.min(Math.round(rect.height * pixelRatio), sourceHeight * 2));

      if (this._canvas.width !== canvasWidth || this._canvas.height !== canvasHeight) {
        this._canvas.width = canvasWidth;
        this._canvas.height = canvasHeight;
        this.scheduleRender();
      }
    }

    async startRuntime() {
      if (this._runtimeStarted || !this._mounted || this._runtimeFailed || this._animationSource === 'static_image') return;
      this._runtimeStarted = true;
      var token = ++this._runtimeToken;

      try {
        var selectedManifest;

        if (this._animationSource === 'built_in_1097') {
          selectedManifest = this.readInlineManifest();
        } else if (this._animationSource === 'custom_manifest') {
          if (!this._customManifestUrl) throw new Error('A custom frame manifest URL is required.');

          var response = await fetch(this._customManifestUrl, {
            cache: 'force-cache',
            credentials: 'same-origin',
            signal: this._lifecycle.signal
          });
          if (!response.ok) throw new Error('Manifest request failed with status ' + response.status + '.');
          selectedManifest = normalizeManifest(await response.json(), response.url || this._customManifestUrl);
        } else {
          throw new Error('Unsupported animation source.');
        }

        if (!this._mounted || token !== this._runtimeToken) return;
        this._manifest = selectedManifest;
        this._frameStore = new FrameStore(
          selectedManifest,
          this.onFrameReady.bind(this),
          this.onFrameError.bind(this)
        );
        this.resizeCanvas();
        if (this._mode === 'zoom') this.requestCurrentFrame();
        this.syncPrefetchState();
      } catch (error) {
        if (!this._mounted || token !== this._runtimeToken) return;
        this.disableFrameInteraction();
        console.warn('[SkyFire beam explorer] Frame interaction is unavailable; the poster remains visible.', error);
      }
    }

    readInlineManifest() {
      var manifestNode = this.querySelector('[data-sf-frame-manifest]');
      if (!manifestNode) throw new Error('The included frame manifest is missing.');
      return normalizeManifest(JSON.parse(manifestNode.textContent), document.baseURI);
    }

    requestCurrentFrame() {
      if (!this._frameStore || !this._manifest) return;
      var target = this.valueToFrameIndex(this._value);
      var direction = target >= this._previousTarget ? 1 : -1;
      this._lastDirection = direction;
      this._previousTarget = target;
      this._frameStore.requestTarget(target, direction);
      this.scheduleRender();
    }

    valueToFrameIndex(value) {
      if (!this._manifest) return 0;
      return Math.round((clamp(value, 0, 100) / 100) * (this._manifest.frames.length - 1));
    }

    onFrameReady() {
      if (!this._mounted) return;
      this._range.disabled = false;
      this._zoomControlsAvailable = true;
      this.updateZoomControlsVisibility();
      this.scheduleRender();
    }

    onFrameError(index, error, failedStore) {
      if (this._animationSource !== 'custom_manifest' || this._customFrameFailureScheduled || failedStore !== this._frameStore) return;
      this._customFrameErrors += 1;
      if (this._customFrameErrors < 2) return;

      this._customFrameFailureScheduled = true;
      var self = this;
      var lifecycle = this._lifecycle;
      queueMicrotask(function () {
        if (!self._mounted || self._lifecycle !== lifecycle || self._frameStore !== failedStore) return;
        console.warn('[SkyFire beam explorer] Custom frames are unavailable; the poster remains visible.', error);
        self.disableFrameInteraction();
      });
    }

    scheduleRender() {
      var self = this;
      if (this._renderRequest) return;
      this._renderRequest = requestAnimationFrame(function () {
        self._renderRequest = 0;
        self.drawCurrentFrame();
      });
    }

    drawCurrentFrame() {
      if (!this._frameStore || !this._manifest || !this._canvas || this._mode !== 'zoom') return;
      var targetIndex = this.valueToFrameIndex(this._value);
      var exactFrame = this._frameStore.getDecoded(targetIndex);
      var result = exactFrame ? {
        index: targetIndex,
        frame: exactFrame
      } : this._frameStore.getClosestDecoded(targetIndex, this._lastDirection, this._displayedFrame);

      if (!result) {
        if (this._nearViewport && this._visible && this._mode === 'zoom') {
          this._frameStore.requestTarget(targetIndex, this._lastDirection);
        }
        return;
      }

      var context = this._canvas.getContext('2d', { alpha: false, desynchronized: true });
      if (!context) return;

      var canvasWidth = this._canvas.width;
      var canvasHeight = this._canvas.height;
      var sourceWidth = this._manifest.width;
      var sourceHeight = this._manifest.height;
      var widthScale = canvasWidth / sourceWidth;
      var heightScale = canvasHeight / sourceHeight;
      var scale = this._zoomMediaFit === 'cover'
        ? Math.max(widthScale, heightScale)
        : Math.min(widthScale, heightScale);
      var drawWidth = sourceWidth * scale;
      var drawHeight = sourceHeight * scale;
      var drawX = (canvasWidth - drawWidth) * (this._zoomPositionX / 100);
      var drawY = (canvasHeight - drawHeight) * (this._zoomPositionY / 100);
      var computedStyle = getComputedStyle(this);
      var mediaBackground = computedStyle.getPropertyValue('--sfmod-media-bg').trim()
        || computedStyle.getPropertyValue('--sf-beam-gradient-start').trim()
        || '#08111b';

      context.fillStyle = mediaBackground;
      context.fillRect(0, 0, canvasWidth, canvasHeight);
      context.drawImage(result.frame.drawable, drawX, drawY, drawWidth, drawHeight);
      this._displayedFrame = result.index;
      this._frameStore.setPinnedIndex(result.index);
      this._zoomStage.dataset.frameReady = 'true';
    }

    updateRangePresentation() {
      var floodLabel = this._range.dataset.floodLabel || 'Flood beam';
      var focusLabel = this._range.dataset.focusLabel || 'Focused beam';
      var activeLabel = this._value < 50 ? floodLabel : focusLabel;
      this._range.style.setProperty('--sf-range-fill', this._value + '%');
      this._range.setAttribute('aria-valuetext', activeLabel + ', ' + Math.round(this._value) + '%');
      if (this._stateLabel) this._stateLabel.textContent = activeLabel;
    }

    selectMode(mode, focusSelected) {
      var nextMode = mode === 'scenes' && this._scenesStage ? 'scenes' : 'zoom';
      this._mode = nextMode;

      this._modeButtons.forEach(function (button) {
        var selected = button.dataset.mode === nextMode;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.tabIndex = 0;
        if (selected && focusSelected) button.focus();
      });

      if (this._zoomStage) this._zoomStage.hidden = nextMode !== 'zoom';
      if (this._scenesStage) this._scenesStage.hidden = nextMode !== 'scenes';
      if (this._scenesControls) this._scenesControls.hidden = nextMode !== 'scenes';
      this.updateZoomControlsVisibility();

      if (nextMode === 'scenes') {
        if (this._frameStore) this._frameStore.pausePrefetch();
        var activeButton = this._sceneButtons.find(function (button) {
          return button.getAttribute('aria-selected') === 'true';
        }) || this._sceneButtons[0];
        if (activeButton) this.selectScene(activeButton.dataset.sceneId, false);
      } else {
        if (this._animationSource !== 'static_image' && !this._runtimeFailed) {
          if (this._nearViewport) this.startRuntime();
          this.scheduleResize();
          this.requestCurrentFrame();
          this.syncPrefetchState();
        }
      }
    }

    selectScene(sceneId, focusSelected) {
      if (!sceneId) return;
      var self = this;

      this._sceneButtons.forEach(function (button) {
        var selected = button.dataset.sceneId === sceneId;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-selected', selected ? 'true' : 'false');
        button.tabIndex = selected ? 0 : -1;
        if (selected && focusSelected) button.focus();
      });

      this._scenePanels.forEach(function (panel) {
        var selected = panel.dataset.sceneId === sceneId;
        panel.hidden = !selected;
        panel.classList.toggle('is-active', selected);
        if (selected) {
          self.hydrateScene(panel);
          panel.classList.remove('is-switching');
          requestAnimationFrame(function () { panel.classList.add('is-switching'); });
        }
      });
    }

    hydrateScene(panel) {
      if (panel.dataset.hydrated === 'true') return;
      if (panel.querySelector('picture, img')) {
        panel.dataset.hydrated = 'true';
        return;
      }
      var template = panel.querySelector('[data-sf-scene-template]');
      if (!template) return;
      panel.appendChild(template.content.cloneNode(true));
      template.remove();
      panel.dataset.hydrated = 'true';
    }

    syncPrefetchState() {
      if (!this._frameStore || !this._manifest) return;
      var targetIndex = this.valueToFrameIndex(this._value);
      var shouldPrefetch = this._nearViewport && this._visible && this._mode === 'zoom';
      if (shouldPrefetch) {
        this._frameStore.resumePrefetch(targetIndex, this._lastDirection);
      } else {
        this._frameStore.pausePrefetch();
      }
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, SkyFireBeamExplorer);
  }
})();
